import React, { Fragment } from "react";
import { Switch, Route, RouteProps } from "react-router-dom";
import { AuthenticatedRoute } from "./@common/auth/components/AuthenticatedRoute";
import _groupBy from "lodash/groupBy";

// Modules containing routes 
import CommonRouting from "./@common/routes";
import HavainnotRouting from "./@havainnot/routes";
import TyoluvatRounting from "./@tyoluvat/routes";
import AuditoinnitRouting from "./@auditoinnit/routes";
import DokumentitRouting from "./@dokumentit/routes";

import RouteErrorBoundary from "./@common/components/RouteErrorBoundary";



export interface IRouteDefinition {
    readonly id: string;
    readonly hrefTemplate?: string;
    readonly path: string;
    readonly exact: boolean;
    readonly render?: React.FC<any>;
    readonly component?: React.FC<any>;
    readonly template?: React.FC<any>;
    readonly requireAuth?: false;
    readonly requiredRoles?: string[];
}

export interface ISubModuleRoutes {
    prefix: string;
    routes: Array<IRouteDefinition>;
    template: React.FC<{}> | null;
    routeId: { [key: string]: string }
}

const modules = [
    AuditoinnitRouting,
    TyoluvatRounting,
    HavainnotRouting,
    DokumentitRouting,
    CommonRouting
]

/**
 * Adds prefix to all route.paths
 */
const withPathPrefix = (modulePrefix: string, routes: Array<IRouteDefinition>): Array<IRouteDefinition> => {
    if (modulePrefix === "") return routes
    const prefix = `/${modulePrefix}`
    return routes.map((route) => ({
        ...route,
        path: route.path === "/" ? prefix : [prefix, route.path].join("")
    }))
};

// Import and concatenate required routes from our modules
const appRoutes: IRouteDefinition[] = [
    ...(modules.reduce<Array<IRouteDefinition>>((acc, module) => {
        return acc.concat(withPathPrefix(module.prefix, module.routes))
    }, [])),
];

export function getRouteHref(route: IRouteDefinition, params: { [key: string]: any } = {}): string {
    const paramNames = Object.keys(params);
    const routeTemplate = route.hrefTemplate || route.path;
    return paramNames.reduce(
        (href, paramName) => href.replace(`:${paramName}`, params[paramName]),
        routeTemplate,
    );
}

/**
 * Return the appropriate Link href for the given route id with the applied
 * parameters. Returns null if the route, for some reason, is not found.
 *
 * @export
* @param {keyof typeof RouteId} id The RouteId of the route we're linking to
* @param {{ [key: string]: any }} params Object containing parameters
* @returns {(string | null)}
        */
export function getRouteHrefById(id: string, params: { [key: string]: any } = {}): string | undefined {
    const route = appRoutes.find((r) => r.id === id);
    if (route) {
        return getRouteHref(route, params);
    } else {
        return undefined;
    }
}

/**
* Renders a <Route> component for the given IRouteDefinition
* @param route The route to render
*/
const renderRoute = (route: IRouteDefinition,): JSX.Element => {


    if (typeof route.requireAuth !== "boolean" || route.requireAuth) {
        return (
            <AuthenticatedRoute key={route.id} {...route} />
        );
    }
    return (
        <Route
            key={route.id}
            path={route.path || undefined}
            exact={route.exact}
            render={route.render}
            component={route.component}
        />
    );
};

export const RouteSwitch: React.FC = () => {
    const modulePrefixes = modules.map((module) => module.prefix);

    const routeGroups = _groupBy(appRoutes, (route) => {
        const prefix = modulePrefixes.find((prefix) => prefix && route.path.startsWith(`/${prefix}`));
        return prefix || "common"
    });

    const renderSubModuleRoutes = (module: ISubModuleRoutes) => {
        const routes = routeGroups[module.prefix || "common"];
        const Template = module.template || Fragment;
        const pathPrefix = module.prefix === "" ? undefined : `/${module.prefix}`;

        if (routes && routes.length) {
            return (
                <Route key={`module-${module.prefix}`} path={pathPrefix} exact={false}>
                    <Template>
                        <Switch children={routes.map((routeProps) => renderRoute(routeProps))} />
                    </Template>
                </Route>
            )
        }
        return null
    }
    return (
        <RouteErrorBoundary rawErrorMessage>
            <Switch>
                {modules.map(renderSubModuleRoutes)}
            </Switch>
        </RouteErrorBoundary>
    );
};
